import { Alert, Box } from '@digibee/beehive-ui';
import map from 'lodash/map';
import React, { FC } from 'react';
import styled from 'styled-components';

import ValidationPropTypes from '../../common/ValidationPropTypes';

import i18n, { t } from '~/common/helpers/i18n';

// Locals

const List = styled.div``;

const Item = styled.div`
  margin-bottom: 5px;
`;

const Notice = styled.div`
  margin-top: 10px;
`;

const DocLink = styled.a`
  text-decoration: underline;
`;

interface AlertObjectsProps {
  objects?: string[];
}

const AlertObjects: FC<AlertObjectsProps> = ({ objects = [] }) => (
  <List>
    {objects?.map(object => (
      <Item key={object}>{object}</Item>
    ))}
  </List>
);

type ValidationPropTypes = {
  code: string;
  message: string;
  details: [
    {
      level: string;
      details: string[];
      code: string;
      message: string;
      objects: string[];
    }
  ];
};

interface DeploymentValidationV2Props {
  validation?: ValidationPropTypes[];
  pipelineId?: string;
}

type AlertVariant = 'info' | 'warning' | 'danger';

/**
 * Analyzer levels come from the backend as INFO / WARN / ERROR, which are not the Alert's variant names. Lowercasing
 * alone silently yields 'warn', an unknown variant that falls back to the neutral style — so warnings rendered as
 * informational notices. Unknown levels stay on 'info', the least alarming option.
 */
const ALERT_VARIANT_BY_LEVEL: Record<string, AlertVariant> = {
  info: 'info',
  warn: 'warning',
  warning: 'warning',
  error: 'danger'
};

const toAlertVariant = (level?: string): AlertVariant =>
  ALERT_VARIANT_BY_LEVEL[level?.toLowerCase() ?? ''] ?? 'info';

/**
 * Analyzer messages arrive already rendered by the backend, in English, from its error_codes.properties. Codes that
 * carry an entry under the `deployment_analysis` namespace are translated here instead, keyed by the code itself so a
 * new one only needs a locale entry. Anything without a translation keeps the backend's own text, which is what every
 * analyzer relied on before.
 */
const isTranslatedAnalysis = (code?: string): boolean =>
  Boolean(code) && i18n.exists(`deployment_analysis.${code}`);

const analysisMessage = (code?: string, fallback?: string): string => {
  if (!code) return fallback ?? '';
  return isTranslatedAnalysis(code)
    ? t(`deployment_analysis.${code}`)
    : (fallback ?? '');
};

/**
 * Present only for the codes whose fix is a redeploy — DGB-172 has no consumer to redeploy — so the notice's absence is
 * expressed by the locale entry's absence rather than by a list of codes kept in step with it here.
 */
const analysisRedeployNotice = (code?: string): string => {
  const key = `deployment_analysis.redeploy.${code}`;
  return code && i18n.exists(key) ? t(key) : '';
};

interface AnalysisAlertContentProps {
  code?: string;
  objects?: string[];
}

const AnalysisAlertContent: FC<AnalysisAlertContentProps> = ({
  code,
  objects
}) => {
  const redeployNotice = analysisRedeployNotice(code);

  return (
    <>
      <AlertObjects objects={objects} />
      {redeployNotice && <Notice>{redeployNotice}</Notice>}
      {isTranslatedAnalysis(code) && (
        <Notice>
          <DocLink
            href={t('deployment_analysis.learn_more_url')}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('deployment_analysis.learn_more_link')}
          </DocLink>
        </Notice>
      )}
    </>
  );
};

function customPipelineError(detail: any, pipelineId?: string) {
  if (
    detail.code === 'DGB-161' &&
    detail.message === 'Pipeline spec size exceeded.'
  ) {
    const canvaUrl = pipelineId
      ? `https://staging.portal.stg.digibee.dev/staging/design/v2/pipelines/${pipelineId}`
      : 'https://staging.portal.stg.digibee.dev/staging/design/v2/pipelines/';
    return {
      ...detail,
      jsxTitle: (
        <>{i18n.t('label.pipeline_has_error_generic_msg_error_learn_more')}</>
      ),
      jsxContent: (
        <>
          {detail.details?.[1] && (
            <div>
              {detail.details[1]}{' '}
              <a
                href={canvaUrl}
                target='_blank'
                rel='noopener noreferrer'
                style={{ textDecoration: 'underline' }}
              >
                {i18n.t('label.review_here_generic_msg_error')}
              </a>
            </div>
          )}
        </>
      )
    };
  }

  return {
    ...detail,
    jsxTitle: analysisMessage(detail.code, detail.message),
    jsxContent: (
      <AnalysisAlertContent code={detail.code} objects={detail.objects} />
    )
  };
}

const DeploymentValidationV2: FC<DeploymentValidationV2Props> = ({
  validation,
  pipelineId
}) => (
  <>
    {map(validation, item =>
      map(item?.details, detail => {
        const customDetail = customPipelineError(detail, pipelineId);
        return (
          <Box key={customDetail.message} css={{ marginBottom: '$2' }}>
            <Alert
              key={customDetail.message}
              variant={toAlertVariant(customDetail?.level)}
              collapsible
              title={customDetail.jsxTitle}
              content={customDetail.jsxContent}
            />
          </Box>
        );
      })
    )}
  </>
);

export default DeploymentValidationV2;
